import { useCallback, useState } from "react";

import { List } from "react-virtualized";
import type { ScrollParams } from "react-virtualized/dist/es/Grid";

import { useResizeObserver } from "app/custom-hooks/useResizeObserver";
import { translate } from "app/filters/i18nFilter";
import { useFilterData } from "app/pages/activation/components/hooks/useFilterData";
import { Item } from "app/pages/activation/components/ProjectsList/Item/Item";
import { FILTER_ITEMS } from "app/pages/activation/components/ProjectsList/ProjectListHeader/config";
import type { ProjectsFilters } from "app/pages/activation/components/ProjectsList/ProjectListHeader/types";
import { useHomeContext } from "app/pages/activation/context/Home/HomeContext";
import type { ActivationSetSchema } from "app/pages/activation/types/api";
import { GrowthbookService, TestsNames } from "app/services/trackingServices/GrowthBookService";
import { TestValues } from "app/services/trackingServices/types";

import { Filters } from "../Shared/Filters/Filters";

import { DEFAULT_LIST_WIDTH, LIST_HEIGHT, LIST_ROW_HEIGHT, SEARCH_DEBOUNCE_MS } from "./constants";
import { listDataAutomation } from "./dataAutomationKeys";
import { ListStates, States } from "./States/States";
import * as SC from "./styles";
import { SEARCH_PLACEHOLDER } from "./texts";

type ProjectListSchema = {
    data: ActivationSetSchema[];
    isError?: boolean;
    isLoading?: boolean;
    isAllAvailableReturned: boolean;
    onClickCreateProject: () => void;
    getAllData: () => void;
};

export const ProjectsList = ({
    data,
    isError,
    isLoading,
    isAllAvailableReturned,
    onClickCreateProject,
    getAllData,
}: ProjectListSchema) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const [listWidth, setListWidth] = useState<number>(DEFAULT_LIST_WIDTH);

    const emptyTestValue = GrowthbookService.growthbookInstance.getFeatureValue(
        TestsNames.ACTIVATION_HOME_PAGE_EMPTY_STATE,
        TestValues.CONTROL,
    );

    const { onSearch, filteredData, onFilter, filtersFlags, activeFilter } = useFilterData(data);

    const { images } = useHomeContext();

    const onResize = useCallback((entry: ResizeObserverEntry) => {
        if (entry.contentRect) {
            setListWidth(entry.contentRect.width);
        }
    }, []);

    useResizeObserver({
        onResize,
        observingElement: container,
    });

    const ListRender = useCallback(
        ({ index, key, style }: { index: number; key: string; style: React.CSSProperties }) => {
            if (!filteredData || !filteredData[index]) {
                return null;
            }

            const project = filteredData[index];
            const icons = [
                images[project.primaryDomain] ?? "",
                ...project.competitorsDomains.map((competitor) => images[competitor] ?? ""),
            ].filter(Boolean);

            return (
                <div
                    style={style}
                    key={key}
                    data-project-id={project.id}
                    data-automation={`project-item-${index}`}
                >
                    <Item
                        project={project}
                        isLoading={isLoading}
                        icons={icons}
                    />
                </div>
            );
        },
        [filteredData, images, isLoading],
    );

    const onScroll = (scrollParams: ScrollParams) => {
        const { clientHeight, scrollHeight, scrollTop } = scrollParams;

        const quarter = Math.floor(scrollHeight * 0.25);
        const isAtBottom = clientHeight + scrollTop >= scrollHeight - quarter;

        if (isAtBottom && !isAllAvailableReturned) {
            getAllData();
        }
    };

    const onFilterWithGetAllData = (id: ProjectsFilters) => {
        getAllData();
        onFilter(id);
    };

    const isProjectsExist = data?.length > 0;
    const isFilteredProjectsExist = filteredData?.length > 0;

    const emptyStates = {
        [TestValues.CONTROL]: States.Empty,
        [TestValues.VARIATION]: States.EmptyVariant,
        [TestValues.VARIATION_2]: States.EmptyList,
    };

    return (
        <SC.ListContainer ref={setContainer}>
            <SC.ListHeader data-automation={listDataAutomation.listHeader}>
                <Filters
                    items={FILTER_ITEMS}
                    flags={filtersFlags}
                    onClick={onFilterWithGetAllData}
                    selectedId={activeFilter}
                    isDisabled={!isProjectsExist}
                />

                <SC.StyledSearchInput
                    data-automation={listDataAutomation.searchInput}
                    placeholder={translate(SEARCH_PLACEHOLDER)}
                    onChange={onSearch}
                    debounce={SEARCH_DEBOUNCE_MS}
                    onFocus={getAllData}
                    disabled={!isProjectsExist}
                />
            </SC.ListHeader>

            {isError ? <ListStates state={States.Error} /> : null}

            {!isError && isFilteredProjectsExist ? (
                <List
                    width={listWidth}
                    height={LIST_HEIGHT}
                    rowHeight={LIST_ROW_HEIGHT}
                    rowCount={filteredData.length}
                    rowRenderer={ListRender}
                    data-automation={listDataAutomation.list}
                    onScroll={onScroll}
                    containerStyle={{ minHeight: `${LIST_HEIGHT}px` }}
                />
            ) : null}

            {!isError && !isFilteredProjectsExist ? (
                <ListStates
                    state={
                        isProjectsExist ? States.EmptyFilteredResult : emptyStates[emptyTestValue ?? TestValues.CONTROL]
                    }
                    onClickCreateProject={onClickCreateProject}
                    listWidth={listWidth}
                />
            ) : null}
        </SC.ListContainer>
    );
};
